import React, {useEffect, createContext, useContext, useRef, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import Section from '../../../../components/section'
import {Box} from '@chakra-ui/react3'
import useIntersectionObserver from '../../../../hooks/use-intersection-observer'

// Create context to track if we're inside a Container and animation state
const StackContext = createContext({isInContainer: false, noAnimation: true})

function Container({children, noAnimation = false}) {
    // this index works like a "stack" where we are pushing (increment)
    // the last fully visible element on top, or we are popping(decrement)
    // the current fully visible element.
    //
    // by default there is no item visible, they will be pushed while the scrolling is happening
    const [fullVisibleIndex, setFullVisibleIndex] = useState(-1)

    // callback to update full visible index with the current full visible Item
    const toggleVisibility = useCallback(({visible, index}) => {
        setFullVisibleIndex((prev) => {
            if (visible) {
                if (index !== prev) {
                    return prev + 1
                }
            } else {
                return Math.max(prev - 1, -1)
            }

            return prev
        })
    }, [])

    return (
        <StackContext.Provider value={{isInContainer: true, noAnimation}}>
            <Section position="relative" paddingBottom="0">
                {React.Children.map(children, (child, index) => {
                    // Check if child is a valid React element and of type Item
                    if (!React.isValidElement(child) || child.type !== Item) {
                        throw new Error(
                            'Stack.Container only accepts Stack.Item as children. ' +
                                `Received: ${child?.type?.name || typeof child}. ` +
                                'Please use Stack.Item components as direct children of Stack.Container.'
                        )
                    }
                    return React.cloneElement(child, {
                        index,
                        toggleVisibility,
                        isFullyVisible: fullVisibleIndex === index
                    })
                })}
            </Section>
        </StackContext.Provider>
    )
}

Container.propTypes = {
    children: PropTypes.node.isRequired,
    noAnimation: PropTypes.bool
}

function Item({children, index, isFullyVisible, toggleVisibility}) {
    const {isInContainer, noAnimation} = useContext(StackContext)
    const highlightRef = useRef(null)
    const firstIntersectionRef = useRef(true)
    const [threshold, setThreshold] = useState(0.8)

    if (!isInContainer) {
        throw new Error(
            'Stack.Item must be used as a child of Stack.Container. ' +
                'Please wrap your Stack.Item components with Stack.Container.'
        )
    }

    useEffect(() => {
        if (!highlightRef.current || noAnimation) return

        const highlightElement = highlightRef.current

        const updateTopProperty = () => {
            const highlightRect = highlightElement.getBoundingClientRect()
            const stickyTopOffset = Math.min(window.innerHeight - highlightRect.height, 0)
            highlightElement.style.top = `${stickyTopOffset}px`
        }

        const calculateThreshold = () => {
            const elementHeight = highlightElement.getBoundingClientRect().height

            if (!elementHeight || elementHeight <= window.innerHeight) {
                return
            }

            const visibleRatio = window.innerHeight / elementHeight
            const nextThreshold = Math.max(0, Math.min(1, visibleRatio - 0.1))
            setThreshold(nextThreshold)
        }

        // resize observer to react properly to container changes, this is useful whenever our
        // content has element like images which can break the top value for the sticky behavior
        // also is helpful for dyanmic visibility threshold
        const resizeObserver = new ResizeObserver(() => {
            calculateThreshold()
            updateTopProperty()
        })

        resizeObserver.observe(highlightElement)

        return () => {
            resizeObserver.disconnect()
        }
    }, [noAnimation])

    useEffect(() => {
        const highlighElement = highlightRef.current

        const focusInHandler = (e) => {
            // only scroll to current item if focus in was using Tab
            if (e.target.matches(':focus-visible')) {
                const containerRect = highlighElement.getBoundingClientRect()
                const highlighHeight = containerRect.height
                const highlightsContainer = highlighElement.parentElement

                window.scrollTo({
                    top: highlightsContainer.offsetTop + index * highlighHeight
                })
            }
        }

        highlighElement.addEventListener('focusin', focusInHandler)

        return () => highlighElement.removeEventListener('focusin', focusInHandler)
    }, [index])

    useIntersectionObserver(
        {
            threshold,
            onChange: (inView) => {
                if (inView) {
                    toggleVisibility({index, visible: true})
                } else {
                    // skip the first intersection after page load by default the
                    // first highlight is "fully visible" even when it is below the fold
                    if (firstIntersectionRef.current) {
                        firstIntersectionRef.current = false
                    } else {
                        toggleVisibility({index, visible: false})
                    }
                }
            }
        },
        highlightRef
    )

    return (
        <Box position={noAnimation ? 'static' : 'sticky'} ref={highlightRef}>
            {React.Children.map(children, (child) =>
                React.cloneElement(child, {isFullyVisible, index})
            )}
        </Box>
    )
}

Item.propTypes = {
    children: PropTypes.node.isRequired,
    index: PropTypes.number,
    isFullyVisible: PropTypes.bool,
    toggleVisibility: PropTypes.func
}

const Stack = {
    Container,
    Item
}

export default Stack
